import * as React from "react";
import { cloneDeep } from "lodash";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useParams } from "react-router";

import FlowFormBase from "../../Base/FlowForm/FlowFormBase";
import chapterDefs from "./chapterDefs";
import descriptions from "./Descriptions";
import chapters from "./Chapters";
import { detailUrl, useBrfPremises } from "../../../../store/brfPremises";
import FullPageSpinner from "../../../Loaders/FullPageSpinner";
import {
  useBrfInvoicingForm,
  constants,
  create,
  destroyPostForm,
} from "../../../../store/invoicingBrf";

import {
  constants as invoicingSettingConstants,
  create as createInvoicingSetting,
  useInvoicingSettingForm,
  useInvoicingSetting,
} from "../../../../store/invoicingSettings";

import {
  setActiveFormInstance,
  updateActiveFormInstance,
  useFormInstanceField,
  useFrequentPermissions,
} from "../../../../store/base";
import { addToast, TOAST_TYPES } from "../../../../store/toasts";

import { useRealEstate } from "../../../../store/realEstates";
import { useCompany } from "../../../../store/companies";
import StandardModal from "../../../Modals/StandardModal";
import { BodyText } from "../../../sharedStyles";
import {
  OverviewTitle,
  OverviewTitleWrapper,
} from "../../../Details/OverviewInfo/styles";

import moment from "moment";
import { useBrfCompany } from "../../../../store/brfCompanies";
import CreateInvoicingSettingBase from "../../Costs/CreateInvoicingSettingBase/CreateInvoicingSettingBase";
import InvoicingSettingBase from "../../Costs/InvoiceSettingBase/InvoiceSettingBase";

const STEPS = {
  INVOICING_BASE: "INVOICING_BASE",
  INVOICING_BASE_CREATE: "INVOICING_BASE_CREATE",
  COSTS: "COSTS",
};

export default function BrfCostsForm({ method = "POST" }) {
  const [step, setStep] = React.useState(STEPS.INVOICING_BASE);
  const dispatch = useDispatch();
  const { replace } = useHistory();
  const storeName = constants.STORE_NAME;
  const [loading, setLoading] = React.useState(false);
  const [alreadyHasInvoicing, setAlreadyHasInvoicing] = React.useState(false);
  const skipInvoicing =
    useFormInstanceField({
      storeName,
      fieldKey: "is_active",
    }) === false;

  const formLoaded = Boolean(useBrfInvoicingForm(method));
  useInvoicingSettingForm(method);
  const { contractId } = useParams();

  const { hasBillectaFullPermission } = useFrequentPermissions();
  const userPermissionsLoaded = useSelector(
    (state) => !!state.app.user?.permissions
  );
  const selectedSetting = useFormInstanceField({
    storeName,
    fieldKey: "setting",
  });

  const selectedBillingCompany = useFormInstanceField({
    storeName,
    fieldKey: "billing_company",
  });

  const billingStartDate = useFormInstanceField({
    storeName,
    fieldKey: "start_date",
  });

  const [brfPremises] = useBrfPremises(contractId);
  const [brfCompany] = useBrfCompany(brfPremises?.brf_company?.id);

  // get company for autofill of invoicing company
  const [realEstate] = useRealEstate(brfPremises?.realestate?.id);
  const [company] = useCompany(realEstate?.company?.id);
  const [companyDefaultInvoicingSetting] = useInvoicingSetting(
    company?.invoicing_config?.id
  );

  React.useEffect(() => {
    if (!userPermissionsLoaded) return;

    if (!hasBillectaFullPermission) {
      setStep(STEPS.COSTS);
    }
  }, [userPermissionsLoaded]);

  React.useEffect(() => {
    return () => {
      dispatch(destroyPostForm(false));
    };
  }, []);

  const onSuccess = (_, returned) => {
    setLoading(false);
    checkout(true);
    replace(detailUrl({ id: brfPremises?.id }));
  };

  const onError = () => {
    setLoading(false);
  };

  const checkout = (success) => {
    dispatch(destroyPostForm(success));
  };

  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, [step]);

  React.useEffect(() => {
    dispatch(
      updateActiveFormInstance({
        storeName,
        data: {
          brf_premis: { id: contractId },
        },
      })
    );
  }, []);

  // auto set billing company
  React.useEffect(() => {
    if (!hasBillectaFullPermission || selectedBillingCompany || !company)
      return;

    dispatch(
      updateActiveFormInstance({
        storeName,
        data: {
          billing_company: company,
          _original_billing_company: company, // for table select component
        },
      })
    );
  }, [company, hasBillectaFullPermission]);

  React.useEffect(() => {
    if (
      !hasBillectaFullPermission ||
      selectedSetting ||
      !companyDefaultInvoicingSetting
    )
      return;

    dispatch(
      updateActiveFormInstance({
        storeName,
        data: {
          setting: companyDefaultInvoicingSetting,
          _original_setting: companyDefaultInvoicingSetting, // for table select component
        },
      })
    );
  }, [companyDefaultInvoicingSetting, hasBillectaFullPermission]);

  // auto set billing start date from contract start date
  React.useEffect(() => {
    if (!hasBillectaFullPermission || !brfPremises || billingStartDate) return;

    dispatch(
      updateActiveFormInstance({
        storeName,
        data: {
          start_date: moment()
            .set({ date: 1 })
            .add(1, "month")
            .format("YYYY-MM-DD"),
        },
      })
    );

    if (brfPremises.brf_invoicing) {
      setAlreadyHasInvoicing(true);
    }
  }, [brfPremises, hasBillectaFullPermission]);

  React.useEffect(() => {
    if (brfPremises?.brf_invoicing?.id) {
      setAlreadyHasInvoicing(true);
    }
  }, [brfPremises]);

  React.useEffect(() => {
    return () => {
      destroyPostForm(false);
    };
  }, []);

  const onSubmit = () => {
    setLoading(true);

    dispatch(
      create({
        successCallback: onSuccess,
        errorCallback: onError,
        preProcess: (data) => preProcess({ data }),
      })
    );
  };

  const onSelectedSetting = () => {
    setStep(STEPS.COSTS);
  };

  // go to create new setting with selected one as base
  const handleCopyAndCreateNew = () => {
    const cleanedExistingSetting = cloneDeep(selectedSetting);
    delete cleanedExistingSetting.id;
    delete cleanedExistingSetting.title;

    if (cleanedExistingSetting.interval_setting) {
      delete cleanedExistingSetting.interval_setting.id;
    }

    if (cleanedExistingSetting.debt_setting) {
      cleanedExistingSetting._original_debt_setting =
        cleanedExistingSetting.debt_setting; // for table select
    }

    dispatch(
      setActiveFormInstance({
        storeName: invoicingSettingConstants.STORE_NAME,
        data: cleanedExistingSetting,
      })
    );

    setStep(STEPS.INVOICING_BASE_CREATE);
    dispatch(
      addToast({
        type: TOAST_TYPES.INFO,
        title: "Aviseringsinställning kopierades",
        description:
          "Uppdatera inställningarna och spara för att skapa en ny inställning",
      })
    );
  };

  // create new setting and then set to active and proceed to next step
  const onCreateNewSetting = () => {
    setLoading(true);
    dispatch(
      createInvoicingSetting({
        preventDefaultToast: true,
        successCallback: (_, returnData) => {
          setLoading(false);

          // set new setting in store
          dispatch(
            updateActiveFormInstance({
              storeName,
              data: {
                setting: returnData,
              },
            })
          );

          dispatch(
            addToast({
              type: TOAST_TYPES.SUCCESS,
              title: "Aviseringsinställning skapades",
              description: "Inställningen valdes för bostadsrätten",
            })
          );

          // go to costs step
          setStep(STEPS.COSTS);
        },
        errorCallback: () => {
          setLoading(false);
          dispatch(
            addToast({
              type: TOAST_TYPES.ERROR,
              title: "Något gick fel",
              description:
                "Inställningen kunde inte skapas. Kontrollera datan och försök igen.",
            })
          );
        },
      })
    );
  };

  const goBackToInvoicingSetup = () => {
    setStep(STEPS.INVOICING_BASE);

    if (company) {
      dispatch(
        updateActiveFormInstance({
          storeName,
          data: {
            billing_company: company,
            _original_billing_company: company, // for table select component
          },
        })
      );

      if (companyDefaultInvoicingSetting) {
        dispatch(
          updateActiveFormInstance({
            storeName,
            data: {
              setting: companyDefaultInvoicingSetting,
              _original_setting: companyDefaultInvoicingSetting, // for table select component
            },
          })
        );
      }
    }
  };

  const topButtonConfig = {
    title: `Hoppa över tillägg ${
      hasBillectaFullPermission ? " & avisering" : ""
    } och gå direkt till bostadsrätt`,
    iconType: "arrow",
    iconPlacement: "right",
    clicked: () => {
      replace(detailUrl({ id: brfPremises.id }));
    },
  };

  return (
    <>
      <StandardModal
        title={`Tillägg ${
          hasBillectaFullPermission ? "& Avisering" : ""
        } redan konfigurerat`}
        isOpen={alreadyHasInvoicing}
        canClose={false}
        actionBarAcceptTitle="Gå till detaljsida"
        withActionBar
        saveFunction={() => {
          replace(detailUrl({ id: brfPremises?.id }));
        }}
        canCancel={false}
      >
        <OverviewTitleWrapper>
          <OverviewTitle>
            Konstnader {hasBillectaFullPermission ? "& Avisering" : ""} redan
            konfigurerat för denna bostadsrätt
          </OverviewTitle>
        </OverviewTitleWrapper>

        <BodyText>
          Denna sida är enbart till för skapande av tillägg
          {hasBillectaFullPermission ? " och aviseringsinställningar" : ""}.
          Denna bostadsrätt har redan en inställning, gå till bostadsrättens
          detaljsida för att redigera tillägg
          {hasBillectaFullPermission ? " och aviseringsinställningar" : ""}.
        </BodyText>
      </StandardModal>

      {(loading || !formLoaded) && <FullPageSpinner />}

      {/* First, if we have billecta permission, we select an invoicing settings or... */}
      {hasBillectaFullPermission &&
        brfPremises &&
        step === STEPS.INVOICING_BASE && (
          <>
            <InvoicingSettingBase
              {...{
                storeName,
                method,
                onSubmit: onSelectedSetting,
                onGoToCreateNew: () => setStep(STEPS.INVOICING_BASE_CREATE),
                onCopyAndCreateNew: handleCopyAndCreateNew,
                contract: brfPremises,
                isBrf: true,
                topButtonConfig,
                title: "Välj aviseringsinställning",
              }}
            />
          </>
        )}

      {/* Create a new one to be used. This renders and invoicing settings form */}
      {hasBillectaFullPermission &&
        brfPremises &&
        step === STEPS.INVOICING_BASE_CREATE && (
          <>
            <CreateInvoicingSettingBase
              {...{
                storeName: invoicingSettingConstants.STORE_NAME,

                method,
                onSubmit: onCreateNewSetting,
                onGoBackToSelect: () => {
                  setStep(STEPS.INVOICING_BASE);
                  dispatch(
                    updateActiveFormInstance({
                      storeName,
                      data: {
                        setting: null, // TODO - set company default here
                      },
                    })
                  );
                },
                title: "Skapa aviseringsinställning",
              }}
            />
          </>
        )}

      {/* Then we can set up the costs and contract specific settings  */}
      {brfPremises && step === STEPS.COSTS && (
        <>
          <FlowFormBase
            {...{
              storeName,
              chapterDefs: chapterDefs({
                hasBillectaFullPermission,
                skipInvoicing,
              }),
              chapters,
              descriptions,
              method,
              onSubmit,
              skipInvoicing,
              goBackToInvoicingSetup,
              contract: brfPremises,
              brfCompany,
              topButtonConfig,
              title: `Tillägg ${
                hasBillectaFullPermission ? "& avisering " : ""
              }för ${brfPremises.str_representation}`,
            }}
          />
        </>
      )}
    </>
  );
}

const preProcess = ({ data }) => {
  const copy = cloneDeep(data);

  return copy;
};
